// Resistances: splitting (EC5 §8.1.4), K_ser, n_ef in shear (EC5 Table 8.1), Johansen single/double shear (EC5 §8.2),
// axial withdrawal and head pull-through (ETA-11/0030).

import { fmtN, foldAcuteAngle } from "./angles";
import type { ScrewRecord } from "./catalog";
import type { EngineTexts } from "./ctx";

// ---- Splitting, EC5 §8.1.4 ---------------------------------------------------------------------------------
// F90,Rk = 14·b·w·√(he/(1−he/h)) [N] (eq. 8.4, corrected 2026-10-12); w = max((wpl/100)^0.35; 1) for punched metal
// plates, 1 for screws; he = distance from the loaded edge to the centre of the farthest fastener.
export function splittingF90Rk(b: number, h: number, he: number, wpl?: number): number {
  const w = Number.isFinite(wpl) ? Math.max(Math.pow((wpl as number) / 100, 0.35), 1) : 1;
  const r = 1 - he / h;
  return r > 1e-9 ? 14 * b * w * Math.sqrt(he / r) : Infinity;
}
// F90,Rd = kmod·F90,Rk/γM (same kmod/γM convention of the shear check).
export function splittingF90Rd(F90Rk: number, kmod: number, gammaM: number): number {
  return (kmod * F90Rk) / gammaM;
}

// ---- K_ser (EN 1995-1-1 §7.1) -------------------------------------------------------------------------------
// 1) ETA-11/0030: HBS/HBSP/SHS/TBS in single shear in LVL softwood, 5≤d≤10 mm: Kser = 60·(d·ρmean/510)^1.5 [N/mm].
// 2) otherwise EC5 Table 7.1 (nails): predrilled ρm^1.5·d/23, non-predrilled ρm^1.5·d^0.8/30.
export function isKserLvlFamily(fam: string | null | undefined): boolean {
  return ["HBS", "HBSP", "SHS", "TBS"].includes((fam || "").toUpperCase());
}
export function etaKserLvl(d: number, rhoMeanLvl: number | null): number | null {
  if (!Number.isFinite(d) || !Number.isFinite(rhoMeanLvl)) return null;
  return 60 * Math.pow((d * (rhoMeanLvl as number)) / 510, 1.5);
}
export function ec5KserNail(d: number, rhoM: number, predrilled: boolean): number | null {
  if (!Number.isFinite(d) || !Number.isFinite(rhoM)) return null;
  return predrilled ? (Math.pow(rhoM, 1.5) * d) / 23 : (Math.pow(rhoM, 1.5) * Math.pow(d, 0.8)) / 30;
}

// ---- n_ef in shear, EC5 §8.3.1.2(4) / Table 8.1, eq. (8.17) ----------------------------------------------------
// n_ef,0 = n^k_ef with k_ef from the REAL step a1 (s1 for element 1, s2 for element 2), linear interpolation;
// a1 ≥ 14d → 1.0; below the first tabulated step → linear EXTRAPOLATION with the first segment slope (user choice).
// Steel-to-timber: a1/0.7 enters the table (equivalent timber-timber step).
const KEF_BREAKPOINTS_NONPREFORATI = [
  { a1d: 7, kef: 0.7 },
  { a1d: 10, kef: 0.85 },
  { a1d: 14, kef: 1.0 },
];
const KEF_BREAKPOINTS_PREFORATI = [
  { a1d: 4, kef: 0.5 },
  { a1d: 7, kef: 0.7 },
  { a1d: 10, kef: 0.85 },
  { a1d: 14, kef: 1.0 },
];
export function kefLookup(a1: number, d: number, predrilled: boolean, steelToTimber: boolean): number | null {
  if (!Number.isFinite(a1) || !Number.isFinite(d) || d <= 0) return null;
  const a1Eff = steelToTimber ? a1 / 0.7 : a1;
  const a1d = a1Eff / d;
  const pts = predrilled ? KEF_BREAKPOINTS_PREFORATI : KEF_BREAKPOINTS_NONPREFORATI;
  if (a1d >= pts[pts.length - 1]!.a1d) return 1.0;
  if (a1d <= pts[0]!.a1d) {
    const p0 = pts[0]!,
      p1 = pts[1]!;
    const slope = (p1.kef - p0.kef) / (p1.a1d - p0.a1d);
    return p0.kef + (a1d - p0.a1d) * slope;
  }
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i]!,
      p1 = pts[i + 1]!;
    if (a1d >= p0.a1d && a1d <= p1.a1d) {
      const t = (a1d - p0.a1d) / (p1.a1d - p0.a1d);
      return p0.kef + t * (p1.kef - p0.kef);
    }
  }
  return 1.0;
}
// Second step (restored 2026-09-30 on user request): linear interpolation between n_ef,0 (force parallel to the
// grain) and n (perpendicular) on the real force/grain angle folded to [0°,90°]. Staggered rows: n directly.
export function nefShearForElement(
  n: number,
  a1: number,
  d: number,
  predrilled: boolean,
  sfalsaViti: boolean,
  angleGrainDeg: number,
  steelToTimber: boolean,
): number | null {
  if (sfalsaViti) return n;
  const kef = kefLookup(a1, d, predrilled, steelToTimber);
  if (kef == null) return null;
  const nef0 = Math.pow(n, kef);
  const angoloAcuto = foldAcuteAngle(angleGrainDeg);
  return nef0 + (n - nef0) * (angoloAcuto / 90);
}

// ---- Johansen ------------------------------------------------------------------------------------------------
export type JohansenVars = {
  d: number;
  t1?: number;
  t2?: number;
  fh1k?: number;
  fh2k?: number;
  tPlate?: number;
  tWood?: number;
  fhk?: number;
  myRk: number;
  faxRk: number;
  beta?: number;
  Fax4: number;
};
// One JohansenResult shape for the three formulas; the double-shear result also carries the per-plane results.
export type JohansenResult = {
  FvRk: number;
  mode?: string | null; // governing Johansen mode ("c"…); steel-timber: see branch (governingLabel)
  modes: Record<string, number>;
  ropes: Record<string, number>;
  vars: JohansenVars;
  // steel-timber only
  branch?: "thin" | "thick" | "interp";
  frac?: number | null;
  thin?: number;
  thick?: number;
  thinMode?: string;
  thickMode?: string;
  // set by compute()
  roles3?: boolean;
  double?: boolean;
  panelFlag?: boolean;
  // double shear (per plane)
  label?: string;
  FaxPlane?: number;
  planes?: JohansenResult[];
  govLabel?: string;
  thread?: { F1: number; F2: number; F3: number; Fhead: number };
};

export function ropeEffect(faxRk: number): number {
  return faxRk / 4;
}
// EC5 §8.2.2(2): the rope effect may not exceed a percentage of the Johansen part of the formula (screws: 100%).
export function ropeCap(fax4: number, johansenPart: number): number {
  return Math.min(fax4, Math.max(0, johansenPart));
}
export function capNote(r: JohansenResult | null | undefined, m: string, t: EngineTexts): string {
  const rp = r && r.ropes ? r.ropes[m] : null;
  return typeof rp === "number" && Number.isFinite(rp) && r!.vars && Number.isFinite(r!.vars.Fax4) && rp < r!.vars.Fax4 - 0.5
    ? t.engine.resistance.ropeCap(fmtN(rp, 0))
    : "";
}
// Governing failure mode ("modo (c)", "lamiera sottile" …), in the user's language.
export function governingLabel(t: EngineTexts, r: JohansenResult): string {
  const tr = t.engine.resistance;
  return r.branch ? tr.steelPlate[r.branch] : tr.mode(r.mode ?? "");
}
// EC5 eq. (8.6) a–f. A missing My,k (NaN) excludes modes d/e/f from the minimum (NaN < x is false).
export function timberTimberSingleShear(
  d: number,
  t1: number,
  t2: number,
  fh1k: number,
  fh2k: number,
  myRk: number,
  faxRk: number,
): JohansenResult {
  const beta = fh2k / fh1k;
  const Fax4 = ropeEffect(faxRk);
  const modes: Record<string, number> = {};
  modes["a"] = fh1k * t1 * d;
  modes["b"] = fh2k * t2 * d;
  const jc =
    ((fh1k * t1 * d) / (1 + beta)) *
    (Math.sqrt(beta + 2 * beta * beta * (1 + t2 / t1 + Math.pow(t2 / t1, 2)) + Math.pow(beta, 3) * Math.pow(t2 / t1, 2)) -
      beta * (1 + t2 / t1));
  const jd =
    ((1.05 * (fh1k * t1 * d)) / (2 + beta)) *
    (Math.sqrt(2 * beta * (1 + beta) + (4 * beta * (2 + beta) * myRk) / (fh1k * d * t1 * t1)) - beta);
  const je =
    ((1.05 * (fh1k * t2 * d)) / (1 + 2 * beta)) *
    (Math.sqrt(2 * beta * beta * (1 + beta) + (4 * beta * (1 + 2 * beta) * myRk) / (fh1k * d * t2 * t2)) - beta);
  const jf = 1.15 * Math.sqrt((2 * beta) / (1 + beta)) * Math.sqrt(2 * myRk * fh1k * d);
  const ropes = { c: ropeCap(Fax4, jc), d: ropeCap(Fax4, jd), e: ropeCap(Fax4, je), f: ropeCap(Fax4, jf) };
  modes["c"] = jc + ropes.c;
  modes["d"] = jd + ropes.d;
  modes["e"] = je + ropes.e;
  modes["f"] = jf + ropes.f;
  let governing: string | null = null,
    FvRk = Infinity;
  for (const [k, val] of Object.entries(modes))
    if (val < FvRk) {
      FvRk = val;
      governing = k;
    }
  return {
    FvRk,
    mode: governing,
    modes,
    ropes,
    vars: { d, t1, t2, fh1k, fh2k, myRk, faxRk, beta, Fax4 },
  };
}
// EC5 eq. (8.7) modes g/h/j/k — double shear (central member t2 between two outer members t1). Fv,Rk is PER SHEAR
// PLANE and per fastener: the screw capacity is n_planes (2) times this value.
export function timberTimberDoubleShear(
  d: number,
  t1: number,
  t2: number,
  fh1k: number,
  fh2k: number,
  myRk: number,
  faxRk: number,
): JohansenResult {
  const beta = fh2k / fh1k;
  const Fax4 = ropeEffect(faxRk);
  const modes: Record<string, number> = {};
  modes["g"] = fh1k * t1 * d;
  modes["h"] = 0.5 * fh2k * t2 * d;
  const jj =
    ((1.05 * (fh1k * t1 * d)) / (2 + beta)) *
    (Math.sqrt(2 * beta * (1 + beta) + (4 * beta * (2 + beta) * myRk) / (fh1k * d * t1 * t1)) - beta);
  const jk = 1.15 * Math.sqrt((2 * beta) / (1 + beta)) * Math.sqrt(2 * myRk * fh1k * d);
  const ropes = { j: ropeCap(Fax4, jj), k: ropeCap(Fax4, jk) };
  modes["j"] = jj + ropes.j;
  modes["k"] = jk + ropes.k;
  let governing: string | null = null,
    FvRk = Infinity;
  for (const [m, val] of Object.entries(modes))
    if (val < FvRk) {
      FvRk = val;
      governing = m;
    }
  return {
    FvRk,
    mode: governing,
    modes,
    ropes,
    vars: { d, t1, t2, fh1k, fh2k, myRk, faxRk, beta, Fax4 },
  };
}
// EC5 §8.2.3: thin plate (t ≤ 0.5d), thick plate (t ≥ d), linear interpolation in between.
export function steelTimberSingleShear(
  d: number,
  tPlate: number,
  tWood: number,
  fhk: number,
  myRk: number,
  faxRk: number,
): JohansenResult {
  const Fax4 = ropeEffect(faxRk);
  const bJ = 1.15 * Math.sqrt(2 * myRk * fhk * d),
    dJ = fhk * tWood * d * (Math.sqrt(2 + (4 * myRk) / (fhk * d * tWood * tWood)) - 1),
    eJ = 2.3 * Math.sqrt(myRk * fhk * d);
  const ropes = { b: ropeCap(Fax4, bJ), d: ropeCap(Fax4, dJ), e: ropeCap(Fax4, eJ) };
  const thinA = 0.4 * fhk * tWood * d,
    thinB = bJ + ropes.b;
  const thickC = fhk * tWood * d;
  const thickD = dJ + ropes.d;
  const thickE = eJ + ropes.e;
  const thin = Math.min(thinA, thinB),
    thick = Math.min(thickC, thickD, thickE);
  const thinMode = thinA <= thinB ? "a" : "b";
  const thickVals: [string, number][] = [
    ["c", thickC],
    ["d", thickD],
    ["e", thickE],
  ];
  let thickMode = "c",
    thickModeVal = thickC;
  for (const [k, val] of thickVals)
    if (val < thickModeVal) {
      thickMode = k;
      thickModeVal = val;
    }
  let FvRk: number, branch: "thin" | "thick" | "interp";
  let frac: number | null = null;
  if (tPlate <= 0.5 * d) {
    FvRk = thin;
    branch = "thin";
  } else if (tPlate >= d) {
    FvRk = thick;
    branch = "thick";
  } else {
    frac = (tPlate - 0.5 * d) / (0.5 * d);
    FvRk = thin + frac * (thick - thin);
    branch = "interp";
  }
  return {
    FvRk,
    branch,
    frac,
    thin,
    thick,
    thinMode,
    thickMode,
    modes: { thinA, thinB, thickC, thickD, thickE },
    ropes,
    vars: { d, tPlate, tWood, fhk, myRk, faxRk, Fax4 },
  };
}

// ---- Axial ---------------------------------------------------------------------------------------------------
// Fax,Rk = fax,k · d · lef · (ρk/ρa)^0.8 (ETA, NOT capped at f_tens,k — user request). fax,k/ρa from the screw DB
// for the substrate/predrilling of the TIP side; lef = L (− washer h, screwLOverride) − thickness crossed before,
// at most the threaded length LG1. 0 (no contribution) when the DB has no value, lef <= 0 or no screw.
export function faxRkVite(
  screwRec: ScrewRecord | null,
  d: number,
  tOpposite: number,
  rhokPointSide: number,
  substrateKey: string | null | undefined,
  preforo: boolean,
  screwLOverride?: number | null,
): number {
  if (!screwRec || !Number.isFinite(rhokPointSide)) return 0;
  const rows = screwRec.estrazioneAssiale.filter((r) => r.substrate === substrateKey);
  if (!rows.length) return 0;
  const row = rows.find((r) => r.preforo === preforo) || rows[0];
  if (!row || typeof row.value_Nmm2 !== "number" || !row.rhoA_kgm3) return 0;
  const { L: LRaw, LG1 } = screwRec.geometria_mm;
  const L = typeof screwLOverride === "number" && Number.isFinite(screwLOverride) ? screwLOverride : LRaw;
  const lef = Math.min(Math.max(L - tOpposite, 0), LG1 || L);
  if (lef <= 0) return 0;
  const rapportoRho = Math.pow(rhokPointSide / row.rhoA_kgm3, 0.8);
  return row.value_Nmm2 * d * lef * rapportoRho;
}

// f_head,k — ETA-11/0030 "Head pull-through capacity" table by family/substrate/panel thickness (some rows are
// for families not in the catalog). null when no row covers the combination (no pull-through limit).
export function etaHeadPullThroughFk(
  family: string,
  d: number,
  substrate: string | null | undefined,
  t: number,
): { value: number; capN?: number } | null {
  const fam = (family || "").toUpperCase();
  const isPanel = substrate === "Panels";
  if (isPanel && Number.isFinite(t) && t < 12) return { value: 8, capN: 400 };
  if (isPanel && Number.isFinite(t) && t < 20) return { value: 8 };
  if (fam === "HBSH" && substrate === "Solidwood Hardwood") {
    if (d === 6) return { value: 28 };
    if (d === 8) return { value: 24 };
    return null;
  }
  if ((fam === "KKF" || fam === "KKT" || fam === "MN") && (substrate === "Solidwood Hardwood" || substrate === "LVL Hardwood")) {
    return { value: 28 };
  }
  if (fam === "HBSH" && substrate === "LVL Hardwood") return { value: 50 }; // Beech LVL/FST ETA-14/0354, ε=60°
  if (substrate === "LVL Softwood") return { value: 20 };
  if (["Solidwood Softwood", "Glulam Softwood", "CLT Softwood", "Panels"].includes(substrate as string)) {
    if (fam === "KKF" || fam === "KKT") return { value: 16.5 };
    if (fam === "TBS" && d === 8) return { value: 15 };
    return { value: 10.5 };
  }
  return null;
}
// Fax,ε,Rk per screw (head pull-through, ETA-11/0030): f_head,k·d_h²·(ρk/ρa)^0.8 (n_ef simplifies away).
// Valid only for ε ≥ 30° (null below). Head/shank condition (all families but KKF/KKT): d_h > 1.8·d_s on the REAL
// head diameter, otherwise 0. d_h = washer outer diameter D2 when a washer is used (always, user request), = d with
// opts.dhAcciaio, else d_k. Panels: ρk ≤ 380 kg/m³ (ETA p. 11).
export function faxCRkPerVite(
  screwRec: ScrewRecord | null,
  rhok: number,
  substrateKey: string | null | undefined,
  t: number,
  epsilonDeg: number,
  opts?: { dhRondella?: number; dhAcciaio?: boolean },
): number | null {
  if (!screwRec || !Number.isFinite(rhok)) return null;
  if (Number.isFinite(epsilonDeg) && foldAcuteAngle(epsilonDeg) < 30) return null;
  const { d1: d, ds, dk } = screwRec.geometria_mm;
  const row = etaHeadPullThroughFk(screwRec.family, d, substrateKey, t);
  if (!row || !Number.isFinite(dk) || !Number.isFinite(ds)) return null;
  const famUpper = (screwRec.family || "").toUpperCase();
  if (famUpper !== "KKF" && famUpper !== "KKT" && dk <= 1.8 * (ds as number)) return 0;
  const dh = opts && opts.dhRondella != null ? opts.dhRondella : opts && opts.dhAcciaio ? d : dk;
  // reference ρa: 350 (softwood/panels), 500 (LVL softwood), 530/730 (hardwood / LVL hardwood)
  const rhoARef = row.value === 20 ? 500 : row.value === 28 || row.value === 50 ? (substrateKey === "LVL Hardwood" ? 730 : 530) : 350;
  const rhoEff = substrateKey === "Panels" ? Math.min(rhok, 380) : rhok;
  let faxCRk = row.value * dh * dh * Math.pow(rhoEff / rhoARef, 0.8);
  if (row.capN != null) faxCRk = Math.min(faxCRk, row.capN);
  return faxCRk;
}
